import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import moment, { Moment } from 'moment';
import { useMemo, useState } from 'react';
import { DashboardFilter } from '../../../components/app/DashboardFilter/DashboardFilter';
import { TimezoneChanger } from '../../../components/app/TimezoneChanger/TimezoneChanger';
import { TimezoneChangerButton } from '../../../components/app/TimezoneChanger/TimezoneChangerButton';
import { PageHeader } from '../../../components/core/PageHeader/PageHeader';
import { useTimezoneDialog } from '../../../hooks/useTimezoneDialog';
import { Timezone } from '../PerformanceDashboard/DashboardTimezoneModal';
import { useStyles } from './DashboardPerformanceHistory.styles';
import { DashboardPerformanceHistoryDataLoader } from './DashboardPerformanceHistoryDataLoader';
import * as assetApi from '../../../api/app/assetApi';
import { AssetTreeView } from '../../../types/app/assetTreeView.types';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Loader } from '../../../components/core/Loader/Loader';
import AssetTreeViewContainer from '../../../components/app/AssetTreeView/AssetTreeView';
import { useTranslation } from '../../../contexts/translation';
import Paper from '@mui/material/Paper';
import { AssetDashboardSetting } from '../../../types/app/asset.types';

export function DashboardPerformanceHistory() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const classes = useStyles();
  const { name, assetId } = useParams();
  const {
    timezone,
    isOpen: isTimezoneDialogOpen,
    setOpen: setTimezoneDialogOpen,
    setTimezone,
  } = useTimezoneDialog();

  // Filtry: TODO: Mohl by bát object
  const [dateFrom, setDateFrom] = useState<Moment>(moment().utc());
  const [dateTo, setDateTo] = useState<Moment>(moment().utc().add(1, 'days').startOf('day'));
  const [granularity, setGranularity] = useState<string>('1h');
  const [selectedAssets, setSelectedAssets] = useState<AssetTreeView[]>([]);
  const [singleCounterAssetId, setSingleCounterAssetId] = useState<number>();

  // Načte strom assetů
  const { data: assetTreeViewData } = useQuery<AssetTreeView>(
    ['assetTreeViewData'],
    () => assetApi.assetGetTreeView(Number(assetId)),
    {
      cacheTime: 0,
    },
  );

  const { data: settingData } = useQuery<AssetDashboardSetting>(['settingForAsset', assetId], () =>
    assetApi.getMainDashBoardSettingForAsset(Number(assetId)),
  );

  // Pokud je jen jeden selectnutý asset, který má jeden counter a není to top asset, tak pro něj getnout nastavení
  const { data: selectedAssetSettingData } = useQuery<AssetDashboardSetting>(
    ['selectedAssetSettingData', selectedAssets[0]?.id],
    () => assetApi.getMainDashBoardSettingForAsset(selectedAssets[0]?.id),
    {
      enabled: !!singleCounterAssetId,
    },
  );

  useEffect(() => {
    let singleCounter = true;
    let counterCount = 0;
    let id = undefined;
    selectedAssets.map((asset: AssetTreeView) => {
      if (asset.counters.length === 0 || !singleCounter) return;
      if (asset.counters.length > 1) return (singleCounter = false);
      if (counterCount === 1) return (singleCounter = false);
      id = asset.id;
      counterCount++;
    });
    setSingleCounterAssetId(id);
    queryClient.invalidateQueries(['selectedAssetSettingData']);
  }, [selectedAssets]);

  // Otevírá okno pro výběr timezone
  const handleTimezoneOpen = () => {
    setTimezoneDialogOpen(true);
  };

  // Změna timezone
  const handleTimezoneChanged = (newTimezone: Timezone) => {
    setTimezone(newTimezone);
    setTimezoneDialogOpen(false);
  };

  // Změna filtru. Typicky datum od do.
  const handleFilterChanged = (dateFrom: Moment, dateTo: Moment, granularity: string) => {
    setDateFrom(dateFrom.clone());
    setDateTo(dateTo.clone());
    setGranularity(granularity);
  };

  // Změna assetu ve treeview
  const handleSelectedAssetsChanged = (selectedAssets: AssetTreeView[]) => {
    setSelectedAssets(selectedAssets);
  };

  const headerButtons = [
    <TimezoneChangerButton key={0} label={timezone.label} onClick={handleTimezoneOpen} />,
  ];

  const assetTreeView = useMemo(() => {
    if (!assetTreeViewData) return <Loader />;
    return (
      <AssetTreeViewContainer
        assetTreeViewData={assetTreeViewData}
        onChange={handleSelectedAssetsChanged}
      />
    );
  }, [assetTreeViewData]);

  const dataLoader = useMemo(() => {
    if (!dateFrom || !dateTo || !assetTreeViewData || !selectedAssets || !settingData)
      return <Loader />;
    return (
      <DashboardPerformanceHistoryDataLoader
        dateFrom={dateFrom.clone().utc()}
        dateTo={dateTo.clone().utc()}
        assetTreeViewData={assetTreeViewData}
        selectedAssets={selectedAssets}
        timezone={timezone}
        granularity={granularity}
        settingData={settingData}
        selectedAssetSettingData={selectedAssetSettingData}
      />
    );
  }, [dateFrom, dateTo, assetTreeViewData, selectedAssets, timezone]);

  // Pro vykreslení komponenty potřebujeme assetTreeView data
  if (!assetTreeViewData || !settingData) return <Loader />;

  return (
    <>
      <PageHeader
        title={t('page.dashboard.history.performance.headline')}
        className={classes.header}
        itemTitle={name}
        itemClassName={classes.itemTitle}
        xs={12}
        buttons={headerButtons}
      />
      <TimezoneChanger
        isOpen={isTimezoneDialogOpen}
        timezone={timezone}
        onChange={handleTimezoneChanged}
        onClose={() => setTimezoneDialogOpen(false)}
      />
      <Grid container spacing={1}>
        <Grid item xs={12} mt={2}>
          <Paper className={classes.paper}>
            <DashboardFilter
              defaultDateFrom={dateFrom}
              defaultDateTo={dateTo}
              settingData={settingData}
              onChange={handleFilterChanged}
            />
            {assetTreeView}
          </Paper>
        </Grid>
        <Grid item xs={12} mt={2}>
          {dataLoader}
        </Grid>
      </Grid>
    </>
  );
}
